import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { checkSession } from '../utils/auth'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'

export default function Login() {
    const [password, setPassword] = useState('')
    const [show, setShow] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const navigate = useNavigate()
    const location = useLocation()

    const redirectTo = location.state?.from || '/notes' // fallback default

    // Auto redirect if already logged in (valid session cookie)
    useEffect(() => {
        let cancelled = false
        checkSession().then((authenticated) => {
            if (!cancelled && authenticated) {
                navigate(redirectTo, { replace: true })
            }
        })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleLogin = async () => {
        setError('')
        if (!password) {
            setError('Please enter the password.')
            return
        }
        setLoading(true)
        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ password }),
            })
            const data = await res.json()
            if (data.ok) {
                navigate(redirectTo, { replace: true })
            } else {
                setError(data.error || 'Incorrect password.')
            }
        } catch (e) {
            setError('Something went wrong. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-start md:pl-[12vw] bg-white dark:bg-zinc-950 transition-colors px-4">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="w-full max-w-sm mx-auto md:mx-0"
            >
                <div className="bg-white dark:bg-zinc-950 border-2 border-black dark:border-white p-6 shadow-brutal">
                    <h2 className="text-xl font-serif font-bold mb-4 text-center text-black dark:text-white">
                        {redirectTo.includes('notes') ? 'Notes Login' : 'Admin Login'}
                    </h2>

                    <form
                        onSubmit={async (e) => { e.preventDefault(); await handleLogin(); }}
                        className="space-y-3"
                        aria-label="Admin login form"
                    >
                        <div>
                            <label htmlFor="password" className="sr-only">Admin password</label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={show ? 'text' : 'password'}
                                    placeholder="Enter admin password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    autoFocus
                                    aria-invalid={!!error}
                                    aria-describedby={error ? 'login-error' : undefined}
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow(v => !v)}
                                    aria-label={show ? 'Hide password' : 'Show password'}
                                    aria-pressed={show}
                                    className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-accent dark:text-zinc-400 dark:hover:text-accent"
                                >
                                    {show ? (
                                        // eye-off icon
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M3.53 2.47 2.47 3.53 6.2 7.27A10.8 10.8 0 0 0 1.64 12s3.6 7 10.36 7a10 10 0 0 0 5.32-1.56l4.15 4.15 1.06-1.06-18-18Zm8.47 15.53c-3.89 0-6.85-2.5-8.53-6 .8-1.73 2-3.1 3.45-4.06l2.01 2.01A4.5 4.5 0 0 0 12 16.5c.91 0 1.76-.27 2.46-.74l1.58 1.58c-.98.48-2.13.69-3.04.66ZM12 7.5c.77 0 1.5.2 2.12.54l1.53 1.53A4.5 4.5 0 0 0 12 7.5Z" /></svg>
                                    ) : (
                                        // eye icon
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M12 5c-6.76 0-10.36 7-10.36 7S5.24 19 12 19s10.36-7 10.36-7S18.76 5 12 5Zm0 12c-3.89 0-6.85-2.5-8.53-6 1.68-3.5 4.64-6 8.53-6s6.85 2.5 8.53 6c-1.68 3.5-4.64 6-8.53 6Zm0-9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" /></svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <p id="login-error" role="alert" className="text-sm text-red-600 dark:text-red-500">
                                {error}
                            </p>
                        )}

                        <Button type="submit" disabled={loading} className="w-full">
                            {loading ? 'Logging in' : 'Login'}
                        </Button>
                    </form>

                    {/* Mobile helpers */}
                    <div className="mt-3 text-center text-xs text-zinc-600 dark:text-zinc-400 font-mono">
                        Press <kbd className="px-1.5 py-0.5 border border-black dark:border-white">Enter</kbd> to submit
                    </div>
                </div>
            </motion.div>
        </div>
    )
}
